import { type FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type Location, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';

import { useGetUserQuarriesForClaimQuery } from '@/api/queries';
import { useCompleteContractMutation } from '@/api/queries/contract';
import { useGetFactoriesQuery } from '@/api/queries/factory';
import { useGetNewLeagueStartedQuery } from '@/api/queries/league';
import { useGetNotificationsAllQuery } from '@/api/queries/notifications';
import { useGetArenaOffersQuery } from '@/api/queries/shop';
import { useExtendedInfoQuery, useGetVipInfoQuery } from '@/api/queries/user';
import { useGetWorkshopQuery } from '@/api/queries/workshop';
import { defaultToastConfig } from '@/common/config/toast';
import {
  setLeagueRequestUntilEndOfDay,
  shouldMakeLeagueRequest,
} from '@/common/lib/requestToGetNewLeagueStarted';
import { ModalContext } from '@/components/Modals/Modal/config';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';
import { useModal } from '@/context/ModalContext';
import { removeMessageNotFoundGroup } from '@/redux/reducers/userReducer';
import {
  getActiveReward,
  getIsBoughtPlan,
  getPaidRewardPlanCost,
} from '@/redux/selectors/earnRewardsSelector';
import { checkIfResourceAvailableToClaim } from '@/redux/selectors/quarrySelectors';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import friendsImage from '/img/icons/frens.png';

type ModalConfig = {
  renderKind:
    | 'claimResources'
    | 'earnReward'
    | 'welcomeArena'
    | 'arenaOfferPopup'
    | 'buyArenaOffer';
  props: unknown | undefined;
  condition: boolean;
  priority?: number;
};

const RequestsAndModalsLogic: FC = () => {
  const { openModal, isOpen } = useContext(ModalContext);
  const { earnRewardModal, setEarnRewardModal } = useModal();
  const showOnboarding = useAppSelector((state) => state.user.showOnboarding);
  const costPlan = useAppSelector(getPaidRewardPlanCost);
  const isBoughtPlan = useAppSelector(getIsBoughtPlan);
  const location = useLocation() as Location<{ modal?: 'takeContract' }>;
  const nextClaimTime = useAppSelector(
    (state) => state.earnRewards.rewards?.nextClaimTime,
  );
  const userBalance = useAppSelector((state) => state.user.user?.balance);
  const availableRegistrationOffer = useAppSelector(
    (state) => state.user.vip?.paidVIPCost.availableRegistrationOffer,
  );
  const shownPopup = useAppSelector((state) => state.shop.mustShowArenaPopup);
  const [isDataReady, setIsDataReady] = useState(false);
  const activeReward = useAppSelector(getActiveReward);
  const isAvailableToClaim = useAppSelector(checkIfResourceAvailableToClaim);
  const isFirstVisit = useAppSelector((state) => state.user.isFirstVisit);
  const isExtendedInfoLoaded = useAppSelector(
    (state) => !!state.user.extendedInfo,
  );
  const showWelcomeArena = useAppSelector(
    (state) => state.user.showWelcomeArena,
  );
  const [skip, setSkip] = useState(false);
  const { isLoading: isQuarriesLoading } = useGetUserQuarriesForClaimQuery(
    undefined,
    { skip },
  );
  useExtendedInfoQuery();
  // TODO add unread count to initial user info
  useGetNotificationsAllQuery({ skip: 0, limit: 0 });
  // useGetPartnersTaskQuery();
  const { isLoading: arenaOffersLoading } = useGetArenaOffersQuery();
  useGetFactoriesQuery(undefined);
  useGetWorkshopQuery();
  const { isLoading } = useGetVipInfoQuery();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const userLevel = useAppSelector((state) => state.user.user?.level);
  const userTelegramId = useAppSelector((state) => state.user.user?.telegramId);
  const notCompletedOffer = useAppSelector((state) =>
    state.shop.profitDropOffers.find((el) => el.isBuyingProcess),
  );
  const groupNotFound = useAppSelector(
    (state) => state.user.user?.groupNotFound,
  );
  const [, { reset }] = useCompleteContractMutation({
    fixedCacheKey: 'shared-complete-contract',
  });

  const { isSuccess, isLoading: isLeagueLoading } = useGetNewLeagueStartedQuery(
    undefined,
    {
      skip: !shouldMakeLeagueRequest() || (userLevel || 0) <= 1,
    },
  );

  useEffect(() => {
    if (isSuccess && shouldMakeLeagueRequest()) {
      setLeagueRequestUntilEndOfDay();
    }
  }, [isSuccess]);

  useEffect(() => {
    return (): void => {
      reset();
    };
  }, []);

  useEffect(() => {
    const timeoutId: NodeJS.Timeout | null = null;

    if (groupNotFound) {
      showToast(
        t(`${groupNotFound}`),
        { img: friendsImage },
        defaultToastConfig,
      );
      dispatch(removeMessageNotFoundGroup());
    }

    // if (
    //   user &&
    //   !sessionStorage.getItem('loggedMessage') &&
    //   Telegram.WebApp.initDataUnsafe.user?.id !== user.telegramId
    // ) {
    //   showToast(
    //     `You are logged as user ${user.firstName} ${user.lastName}`,
    //     undefined,
    //     defaultToastConfig,
    //   );
    //   timeoutId = setTimeout(() => {
    //     sessionStorage.setItem('loggedMessage', 'true');
    //   }, 100);
    // }

    return (): void => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [groupNotFound]);

  // Додаємо стан для відстеження готовності всіх даних

  // Перевіряємо завершення всіх запитів
  useEffect(() => {
    if (
      !isQuarriesLoading &&
      !isLeagueLoading &&
      !arenaOffersLoading &&
      isExtendedInfoLoaded
    ) {
      setIsDataReady(true);
    }
  }, [
    isQuarriesLoading,
    isLeagueLoading,
    isExtendedInfoLoaded,
    arenaOffersLoading,
  ]);

  // Обробляємо чергу тільки після готовності даних
  useEffect(() => {
    // Зупиняємо обробку, якщо дані ще не готові
    if (!isDataReady || isLoading || showOnboarding) return;

    // Формуємо чергу модалок із умовами
    const queue: ModalConfig[] = [
      {
        renderKind: 'claimResources',
        props: undefined,
        condition: !!(
          location.key === 'default' &&
          isAvailableToClaim &&
          !sessionStorage.getItem('isAllResourcesClaimed') &&
          !isFirstVisit &&
          // in Telegram: only for the account of this Telegram user;
          // the native app has no Telegram context
          (Capacitor.isNativePlatform() ||
            Telegram.WebApp.initDataUnsafe.user?.id === userTelegramId)
        ),
      },
      {
        renderKind: 'buyArenaOffer',
        props: {
          arenaOffer: notCompletedOffer,
          autoOpened: true,
        },
        condition: !!notCompletedOffer,
        priority: 1,
      },
      {
        renderKind: 'earnReward',
        props: undefined,
        condition: !!(
          activeReward &&
          nextClaimTime === 0 &&
          !sessionStorage.getItem('isRewardClosed') &&
          (!costPlan || isBoughtPlan)
        ),
      },
      {
        renderKind: 'welcomeArena',
        props: undefined,
        condition: !!showWelcomeArena,
      },
      {
        renderKind: 'arenaOfferPopup',
        props: undefined,
        condition: shownPopup,
        priority: 2,
      },
    ];

    // Якщо жодна модалка не відкрита, обробляємо чергу
    if (!isOpen && !earnRewardModal) {
      const nextModal = queue
        .filter((modal) => modal.condition)
        .sort((a, b) => {
          const aPriority = a.priority ?? Number.MAX_SAFE_INTEGER;
          const bPriority = b.priority ?? Number.MAX_SAFE_INTEGER;

          return aPriority - bPriority;
        })[0];

      if (nextModal) {
        if (nextModal.renderKind === 'earnReward') {
          setEarnRewardModal(true);
        } else {
          openModal({
            renderKind: nextModal.renderKind,
            props: nextModal.props as never,
          });
        }

        if (nextModal.renderKind === 'claimResources') {
          setSkip(true);
        }
      }
    }
  }, [
    isDataReady,
    isOpen,
    earnRewardModal,
    userTelegramId,
    isAvailableToClaim,
    isFirstVisit,
    activeReward,
    nextClaimTime,
    showWelcomeArena,
    location.key,
    availableRegistrationOffer,
    userBalance,
    isLoading,
    shownPopup,
    showOnboarding,
  ]);

  return null;
};

export { RequestsAndModalsLogic };
